import { formatAppointmentTime, toBusinessDateKey } from "@zapis24/utils";

export type HomeTargetTab = "appointments" | "services" | "marketplace" | "settings" | "site";
export type HomeSettingsSection = "schedule" | "notifications" | "payment";
export type HomePlanStatus = "FREE_LIMITED" | "ACTIVE" | "PAST_DUE" | "CANCELLED";
export type HomePlanCardTone = "normal" | "success" | "warning" | "danger";

export interface HomeAppointmentOpenTarget {
  appointmentId: string;
  date: string;
}

export interface HomeAppointmentOpenInput {
  id: string;
  startsAt: string | Date;
}

export interface HomePrimaryAppointmentInput {
  startsAt: string | Date;
  endsAt: string | Date;
}

export interface HomeTodayAppointmentInput {
  id: string;
  startsAt: string | Date;
}

export interface HomeDashboardDateLabelInput {
  todayDate: string;
  nextAppointment: HomePrimaryAppointmentInput | null;
  timeZone?: string;
}

export type HomePrimaryAppointmentState =
  | { kind: "empty"; label: "Сегодня свободно" }
  | { kind: "current"; label: "Сейчас идет" }
  | { kind: "next"; label: "Следующая запись" }
  | { kind: "past"; label: "Запись прошла" };

export interface HomeFreeWindowInput {
  timeStr: string;
}

export interface HomePlanAccessCard {
  eyebrow: string;
  title: string;
  body: string;
  buttonLabel: string;
  tone: HomePlanCardTone;
}

export interface HomeUrgentActionInput {
  id: string;
  targetTab: HomeTargetTab;
}

export type HomeUrgentActionPresentation =
  | {
      kind: "overdueAppointments";
      isCollapsible: true;
      buttonLabel: null;
    }
  | {
      kind: "openTab";
      isCollapsible: false;
      buttonLabel: string;
    };

export type HomeOverdueAppointmentCardActionId =
  | "complete"
  | "noShow"
  | "notify"
  | "cancel";

export interface HomeOverdueAppointmentCardActionsInput {
  canComplete: boolean;
  canMarkNoShow: boolean;
  canContact: boolean;
  canCancel: boolean;
  canReschedule?: boolean;
}

export interface HomeOverdueAppointmentCardAction {
  id: HomeOverdueAppointmentCardActionId;
  label: string;
  prominence: "primary" | "secondary" | "dangerSubtle";
}

export type LaunchChecklistStepId =
  | "services"
  | "schedule"
  | "site"
  | "notifications"
  | "payment"
  | "test-booking";

export interface LaunchChecklistStep {
  id: LaunchChecklistStepId;
  title: string;
  body: string;
  targetTab: HomeTargetTab;
  settingsSection?: HomeSettingsSection;
  buttonLabel: string;
}

export interface LaunchChecklistProgress {
  completed: number;
  total: number;
  percent: number;
  label: string;
  isComplete: boolean;
}

export const LAUNCH_CHECKLIST_STEPS: LaunchChecklistStep[] = [
  {
    id: "services",
    title: "Проверить услуги",
    body: "Названия, длительность, цены и активность услуг должны совпадать с реальным прайсом.",
    targetTab: "services",
    buttonLabel: "Услуги",
  },
  {
    id: "schedule",
    title: "Настроить график",
    body: "Рабочие дни, часы приема и временные закрытия формируют свободные окна для клиентов.",
    targetTab: "settings",
    settingsSection: "schedule",
    buttonLabel: "График",
  },
  {
    id: "site",
    title: "Оформить сайт",
    body: "Заголовок, подзаголовок, тема и кнопки связи должны выглядеть понятно для клиента.",
    targetTab: "site",
    buttonLabel: "Сайт",
  },
  {
    id: "notifications",
    title: "Подключить уведомления",
    body: "Выберите Telegram или MAX, чтобы новые записи и переносы приходили мастеру в рабочий чат.",
    targetTab: "settings",
    settingsSection: "notifications",
    buttonLabel: "Уведомления",
  },
  {
    id: "payment",
    title: "Добавить СБП",
    body: "Телефон и банки получателя появятся у клиента в деталях записи, если мастер принимает переводы.",
    targetTab: "settings",
    settingsSection: "payment",
    buttonLabel: "СБП",
  },
  {
    id: "test-booking",
    title: "Сделать тестовую запись",
    body: "Проверьте путь клиента: сайт, услуга, свободное время, запись и появление записи в расписании.",
    targetTab: "appointments",
    buttonLabel: "Расписание",
  },
];

const WEEKDAYS_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const WEEKDAYS_LONG = [
  "воскресенье",
  "понедельник",
  "вторник",
  "среда",
  "четверг",
  "пятница",
  "суббота",
];
const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

export function getHomeActionButtonLabel(targetTab: HomeTargetTab): string {
  const labels: Record<HomeTargetTab, string> = {
    appointments: "Открыть записи",
    services: "Открыть услуги",
    marketplace: "Открыть выгодное",
    settings: "Открыть настройки",
    site: "Открыть сайт",
  };

  return labels[targetTab];
}

export function getLaunchChecklistStorageKey(businessId: string): string {
  return `zapis24:home-launch-checklist:${businessId}:v1`;
}

export function getLaunchChecklistProgress(checkedIds: readonly string[]): LaunchChecklistProgress {
  const validIds = new Set(LAUNCH_CHECKLIST_STEPS.map((step) => step.id));
  const completed = new Set(checkedIds.filter((id) => validIds.has(id as LaunchChecklistStepId))).size;
  const total = LAUNCH_CHECKLIST_STEPS.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return {
    completed,
    total,
    percent,
    label: `${completed} из ${total}`,
    isComplete: total > 0 && completed === total,
  };
}

export function getHomeUrgentActionPresentation(
  action: HomeUrgentActionInput,
): HomeUrgentActionPresentation {
  if (action.id === "finish-overdue") {
    return {
      kind: "overdueAppointments",
      isCollapsible: true,
      buttonLabel: null,
    };
  }

  return {
    kind: "openTab",
    isCollapsible: false,
    buttonLabel: getHomeActionButtonLabel(action.targetTab),
  };
}

export function getHomeOverdueAppointmentCardActions(
  availability: HomeOverdueAppointmentCardActionsInput,
): HomeOverdueAppointmentCardAction[] {
  const actions: HomeOverdueAppointmentCardAction[] = [];

  if (availability.canComplete) {
    actions.push({
      id: "complete",
      label: "Завершить",
      prominence: "primary",
    });
  }

  if (availability.canMarkNoShow) {
    actions.push({
      id: "noShow",
      label: "Не пришёл",
      prominence: "secondary",
    });
  }

  if (availability.canContact) {
    actions.push({
      id: "notify",
      label: "Написать",
      prominence: "secondary",
    });
  }

  if (availability.canCancel) {
    actions.push({
      id: "cancel",
      label: "Отменить",
      prominence: "dangerSubtle",
    });
  }

  return actions;
}

export function getHomeAppointmentOpenTarget(
  appointment: HomeAppointmentOpenInput,
  timeZone?: string,
): HomeAppointmentOpenTarget {
  const startsAt =
    appointment.startsAt instanceof Date
      ? appointment.startsAt
      : new Date(appointment.startsAt);

  if (Number.isNaN(startsAt.getTime())) {
    throw new Error("Invalid appointment startsAt");
  }

  return {
    appointmentId: appointment.id,
    date: toBusinessDateKey(startsAt, timeZone),
  };
}

export function getHomeDashboardDateLabel({
  todayDate,
  nextAppointment,
  timeZone,
}: HomeDashboardDateLabelInput): string {
  const nextDate = nextAppointment
    ? toBusinessDateKey(nextAppointment.startsAt, timeZone)
    : todayDate;
  const labelDate = nextDate !== todayDate ? nextDate : todayDate;
  const label = formatLongDateKey(labelDate);

  return nextDate !== todayDate ? `ближайшая: ${label}` : label;
}

export function getHomePrimaryAppointmentTimeLabel(
  appointment: HomePrimaryAppointmentInput,
  todayDate: string,
  timeZone?: string,
): string {
  const startsAt = readDate(appointment.startsAt);
  const endsAt = readDate(appointment.endsAt);
  const timeLabel = `${formatAppointmentTime(startsAt, timeZone)} - ${formatAppointmentTime(endsAt, timeZone)}`;
  const appointmentDate = toBusinessDateKey(startsAt, timeZone);

  if (appointmentDate === todayDate) {
    return timeLabel;
  }

  const compactDate = formatCompactDateKey(appointmentDate);
  if (appointmentDate === addDaysToDateKey(todayDate, 1)) {
    return `Завтра, ${compactDate} · ${timeLabel}`;
  }

  return `${capitalizeRu(compactDate)} · ${timeLabel}`;
}

export function getHomePrimaryAppointmentState(
  appointment: HomePrimaryAppointmentInput | null,
  now = new Date(),
): HomePrimaryAppointmentState {
  if (!appointment) {
    return { kind: "empty", label: "Сегодня свободно" };
  }

  const startsAt = readDate(appointment.startsAt);
  const endsAt = readDate(appointment.endsAt);

  if (startsAt <= now && endsAt > now) {
    return { kind: "current", label: "Сейчас идет" };
  }

  if (endsAt <= now) {
    return { kind: "past", label: "Запись прошла" };
  }

  return { kind: "next", label: "Следующая запись" };
}

export function getFreeWindowPreview(
  windows: HomeFreeWindowInput[],
  limit = 3,
): string[] {
  return windows.slice(0, limit).map((window) => window.timeStr);
}

export function getOtherTodayAppointments<T extends HomeTodayAppointmentInput>(
  appointments: T[],
  primaryAppointment: HomeTodayAppointmentInput | null | undefined,
): T[] {
  if (!primaryAppointment) {
    return appointments
      .slice()
      .sort((a, b) => readDate(a.startsAt).getTime() - readDate(b.startsAt).getTime());
  }

  const primaryStartsAt = readDate(primaryAppointment.startsAt);

  return appointments
    .filter((appointment) => {
      if (appointment.id === primaryAppointment.id) return false;
      return readDate(appointment.startsAt) > primaryStartsAt;
    })
    .slice()
    .sort((a, b) => readDate(a.startsAt).getTime() - readDate(b.startsAt).getTime());
}

export function getOtherTodayToggleLabel(count: number, expanded: boolean): string {
  if (expanded) return "Свернуть остальные записи";
  return `Показать ещё ${formatAppointmentCount(count)}`;
}

export function getPlanAccessCard(input: {
  planStatus: HomePlanStatus;
  planExpiresAt: string | null | undefined;
  now: Date;
}): HomePlanAccessCard {
  if (input.planStatus === "CANCELLED") {
    return {
      eyebrow: "Доступ отключен",
      title: "Онлайн-запись остановлена",
      body: "Доступ к новым записям временно отключен оператором платформы.",
      buttonLabel: "Настройки",
      tone: "danger",
    };
  }

  return {
    eyebrow: "Бесплатный доступ",
    title: "Запись24 бесплатен для мастеров",
    body: "Основные инструменты кабинета и онлайн-запись доступны без лимита записей.",
    buttonLabel: "Выгодно",
    tone: "success",
  };
}

function readDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

function formatCompactDateKey(dateKey: string): string {
  const parts = parseDateKey(dateKey);
  if (!parts) return dateKey;

  const [year, month, day] = parts;
  const weekday = WEEKDAYS_SHORT[getDateKeyWeekday(year, month, day)] ?? "";
  const monthName = MONTHS_GENITIVE[month - 1] ?? "";

  return `${weekday} ${day} ${monthName}`;
}

function formatLongDateKey(dateKey: string): string {
  const parts = parseDateKey(dateKey);
  if (!parts) return dateKey;

  const [year, month, day] = parts;
  const weekday = WEEKDAYS_LONG[getDateKeyWeekday(year, month, day)] ?? "";
  const monthName = MONTHS_GENITIVE[month - 1] ?? "";

  return `${weekday}, ${day} ${monthName}`;
}

function parseDateKey(dateKey: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;

  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function getDateKeyWeekday(year: number, month: number, day: number): number {
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function addDaysToDateKey(dateKey: string, days: number): string {
  const parts = parseDateKey(dateKey);
  if (!parts) return dateKey;

  const [year, month, day] = parts;
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return [
    String(date.getUTCFullYear()).padStart(4, "0"),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function capitalizeRu(value: string): string {
  return value ? `${value[0]?.toLocaleUpperCase("ru-RU")}${value.slice(1)}` : value;
}

function formatAppointmentCount(count: number): string {
  const abs = Math.abs(count);
  const mod10 = abs % 10;
  const mod100 = abs % 100;

  if (mod10 === 1 && mod100 !== 11) return `${count} запись`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} записи`;
  return `${count} записей`;
}
